import type { BottomTabNavigationOptions } from '@react-navigation/bottom-tabs';
import type { DrawerNavigationOptions } from '@react-navigation/drawer';
import type { NativeStackNavigationOptions } from '@react-navigation/native-stack';
import { BookOpen, Users, UserCircle, Coffee } from 'lucide-react-native';

// Navigation configuration for consistent behavior across the app
export const navigationConfig = {
  // Main tab routes that should show bottom navigation
  mainTabRoutes: ['recipes', 'creators', 'equipment', 'profile'] as const,

  // Routes that should show back button (detail/child routes only)
  // These are child screens within stacks that should always show back button
  detailRoutes: [
    // Recipe stack children
    'recipeDetail',
    'createRecipe',
    'roasters',
    // Creator stack children
    'creatorProfile',
    'creatorRecipes',
    // Equipment stack children
    'equipmentDetail',
    'equipmentCreate',
    // Profile stack children
    'editProfile',
    'myRecipes',
    'myComments',
    'notificationSettings',
    'privacySecurity',
    'deleteAccount',
    'favorites',
    // Journey stack children
    'weeklyGoals',
    'activityDetail',
    'progressTracker',
    'createJourney',
    // AI Experience stack children
    'aiOnboarding',
    'aiJourneyFlow',
    'aiAdaptiveLearning',
    'aiPersonalization',
    'aiAnalytics',
    'socialLearningAnalytics',
    'intelligentSearch',
    'aiAssessment',
    'aiTutoringDashboard',
    'premiumAnalyticsDashboard',
    'peerMatching',
    'adaptiveProgress',
    'progressInsights',
    'notificationCenter',
    'masteryVisualization',
    // Top-level drawer screens that should show back (navigated from sidebar)
    'settings',
    'helpSupport',
  ] as const,

  // Routes that are parent/index screens (never show back button)
  // These are the root screens of each stack or top-level navigation items
  parentStackRoutes: [
    'recipes',
    'creators',
    'equipment',
    'profile',
    'home',
    'index', // Generic index route name used in stacks
    'recommendations',
    'termsAndConditions',
    'dashboardTest',
  ] as const,

  // Routes that are drawer-level features but their index screens should NOT show back
  // Only their child screens should show back
  drawerFeatureRoutes: ['journey', 'aiExperience'] as const,

  // Routes that should be in drawer/hamburger menu
  drawerRoutes: [
    'recommendations',
    'journey',
    'aiExperiments',
    'settings',
    'termsAndConditions',
    'helpSupport',
  ] as const,

  // Routes that should be hidden from main navigation
  hiddenRoutes: ['home', 'dashboardTest'] as const,

  // Screen size breakpoints
  breakpoints: {
    mobile: 768,
    tablet: 1024,
    desktop: 1200,
  },

  // Animation configurations
  animations: {
    slide: {
      gestureEnabled: true,
      animationTypeForReplace: 'push' as const,
      animation: 'slide_from_right' as const,
    },
    modal: {
      presentation: 'modal' as const,
      animationTypeForReplace: 'pop' as const,
    },
  },
} as const;

// Tab bar icon mapping
export const tabBarIcons = {
  recipes: BookOpen,
  creators: Users,
  equipment: Coffee,
  profile: UserCircle,
} as const;

// Default screen options for different navigator types
export const defaultScreenOptions = {
  stack: {
    headerShown: true,
    gestureEnabled: true,
    animationTypeForReplace: 'push',
  } as NativeStackNavigationOptions,

  tab: {
    headerShown: false,
    tabBarHideOnKeyboard: true,
    tabBarStyle: {
      paddingBottom: 8,
      paddingTop: 8,
      height: 60,
    } as const,
    tabBarLabelStyle: {
      fontSize: 12,
      fontWeight: '500' as const,
    } as const,
  } as BottomTabNavigationOptions,

  drawer: {
    headerShown: false,
    drawerType: 'front',
    swipeEnabled: true,
  } as DrawerNavigationOptions,
} as const;

// Route title mapping for consistent titles
export const routeTitles = {
  recommendations: 'Recommendations',
  recipes: 'Recipes',
  creators: 'Creators',
  equipment: 'Equipment',
  profile: 'Profile',
  recipeDetail: 'Recipe',
  creatorProfile: 'Creator',
  creatorRecipes: 'Recipes',
  userProfile: 'User Profile',
  equipmentDetail: 'Equipment Details',
  equipmentCreate: 'Create Equipment',
  createRecipe: 'Create Recipe',
  editProfile: 'Edit Profile',
  myRecipes: 'My Recipes',
  myComments: 'My Comments',
  settings: 'Settings',
  notificationSettings: 'Notification Settings',
  privacySecurity: 'Privacy & Security',
  helpSupport: 'Help & Support',
  deleteAccount: 'Delete Account',
  favorites: 'Favorites',
  roasters: 'Roasters',
  journey: 'Journey',
  aiExperiments: 'AI Experiments (Alpha)',
  aiExperience: 'AI Experiments (Alpha)',
  termsAndConditions: 'Terms & Conditions',
  home: 'Home',
  dashboardTest: 'Dashboard Test',
} as const;

// Helper function to check if route is a main tab
export function isMainTabRoute(routeName: string): boolean {
  return navigationConfig.mainTabRoutes.includes(routeName as any);
}

// Helper function to check if route should show back button
// Parent/index routes should NEVER show back button
// Child/detail routes should ALWAYS show back button
export function shouldShowBackButton(routeName: string): boolean {
  // Never show back button for main tabs
  if (navigationConfig.mainTabRoutes.includes(routeName as any)) {
    return false;
  }

  // Never show back button for parent/index routes
  if (navigationConfig.parentStackRoutes.includes(routeName as any)) {
    return false;
  }

  // Never show back button for drawer feature index screens
  // (journey and aiExperience index screens - they're accessed from sidebar)
  if (navigationConfig.drawerFeatureRoutes.includes(routeName as any)) {
    return false;
  }

  // Show back button for all detail/child routes
  if (navigationConfig.detailRoutes.includes(routeName as any)) {
    return true;
  }

  // Default: don't show back button for unknown routes
  return false;
}

// Helper to check if a route is a child screen within a feature stack
export function isChildRoute(routeName: string): boolean {
  return navigationConfig.detailRoutes.includes(routeName as any);
}

// Helper function to get route title
export function getRouteTitle(routeName: string): string {
  return routeTitles[routeName as keyof typeof routeTitles] || routeName;
}
